import { useId, useRef, useState } from 'react';

import styles from './OtpInput.module.css';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Вызывается, когда набран последний символ, — автоматическая проверка. */
  onComplete: (value: string) => void;
  length: number;
  label: string;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  describedBy?: string | undefined;
  autoFocus?: boolean | undefined;
}

/**
 * Ввод одноразового кода: одно поле, визуально разделённое на ячейки.
 *
 * Почему одно поле, а не шесть (MVP_PRD §8 допускает оба варианта):
 * вставка кода целиком, автозаполнение из письма (`autocomplete="one-time-code"`)
 * и работа скринридера получаются бесплатно от браузера. Шесть отдельных полей
 * пришлось бы учить перекладывать вставленный текст, переносить фокус по
 * Backspace и объяснять скринридеру, что это одно значение.
 *
 * Настоящий `<input>` растянут поверх ячеек и прозрачен: клик по любой ячейке
 * попадает в него, каретка браузера скрыта, а её роль играет подсвеченная ячейка.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length,
  label,
  disabled = false,
  invalid = false,
  describedBy,
  autoFocus = false,
}: OtpInputProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const digits = Array.from({ length }, (_, index) => value[index] ?? '');
  // Активная ячейка: следующая незаполненная, а на полном коде — последняя.
  const activeIndex = Math.min(value.length, length - 1);

  const handleChange = (raw: string) => {
    const next = raw.replace(/\D/g, '').slice(0, length);
    if (next === value) return;
    onChange(next);
    if (next.length === length) onComplete(next);
  };

  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>

      <div
        className={[
          styles.cells,
          focused && styles.cellsFocused,
          invalid && styles.cellsInvalid,
          disabled && styles.cellsDisabled,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {digits.map((digit, index) => (
          <span
            key={index}
            className={[
              styles.cell,
              digit && styles.cellFilled,
              focused && !disabled && index === activeIndex && styles.cellActive,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          >
            {digit || <span className={styles.caret} />}
          </span>
        ))}

        <input
          ref={inputRef}
          id={inputId}
          className={styles.input}
          value={value}
          disabled={disabled}
          // Мобильная клавиатура сразу цифровая, iOS подставляет код из письма.
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={length}
          autoFocus={autoFocus}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(event) => {
            handleChange(event.target.value);
          }}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onSelect={() => {
            // Каретка всегда в конце: середину кода редактировать незачем,
            // а произвольная позиция рассинхронизировала бы подсветку ячеек.
            const input = inputRef.current;
            if (input && input.selectionStart !== input.value.length) {
              input.setSelectionRange(input.value.length, input.value.length);
            }
          }}
        />
      </div>
    </div>
  );
}
